// src/pages/Login.js
import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Alert,
  Typography,
  Link,
  Paper,
  CircularProgress,
  useTheme
} from '@mui/material';
import {
  Login as LoginIcon,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';

import { getData, saveSessionItem } from '../services/storageService';
import { useI18n } from '../i18n/i18n';

export default function Login({ onLogin }) {
  const { t } = useI18n();
  const theme = useTheme();
  
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const users = await getData('users') || [];
      const foundUser = users.find(
        u => u.username.toLowerCase() === username.toLowerCase() && u.password === password
      );

      if (foundUser) {
        await saveSessionItem('currentUser', foundUser);
        onLogin(foundUser); 
      } else {
        setError(t('login_error'));
        setLoading(false); 
      }
    } catch (error) {
      console.error('Login error:', error);
      setError(t('error_closing_shift'));
      setLoading(false); 
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2
      }}
    >
      <Paper
        elevation={8}
        sx={{
          p: 4, // ЗМЕНШЕНО: було 4, стало 3 (компактніші відступи)
          borderRadius: 3,
          maxWidth: 425, // ЗМЕНШЕНО: було 450, стало 400 (ефект масштабу 90%)
          width: '100%',
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)'
        }}
      >
        {/* Заголовок та Логотип */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          {/* ЛОГОТИП: Замість Avatar використовуємо img */}
          <Box 
            component="img"
            src="/lainsa-logo.png"
            alt="Lainsa Logo"
            sx={{
              height: 'auto',
              maxWidth: '180px', // Обмеження ширини логотипу, щоб він виглядав гармонійно
              mb: 1,
              display: 'block',
              mx: 'auto' // Центрування
            }}
          />
          
          {/* Якщо текст "Lainsa" вже є на картинці логотипу, цей заголовок можна прибрати або зменшити */}
          {/* <Typography variant="h5" component="h1" sx={{ fontWeight: 700, color: theme.palette.primary.dark }}>
            {t('brand')}
          </Typography> */}
          
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('app_subtitle')}
          </Typography>
        </Box>

        {/* Форма входу */}
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 2,
                borderRadius: 2,
                fontSize: '0.875rem' // Трохи зменшений шрифт помилки
              }}
            >
              {error}
            </Alert>
          )}

          <TextField
            label={t('labels_operator') + " *"}
            variant="outlined"
            margin="dense" // ЗМЕНШЕНО: normal -> dense (менші відступи між полями)
            fullWidth
            required
            autoFocus
            value={username}
            onChange={e => setUsername(e.target.value)}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              }
            }}
            disabled={loading}
          />

          <TextField
            label={t('password_label') + " *"}
            type={showPassword ? 'text' : 'password'}
            variant="outlined"
            margin="dense" // ЗМЕНШЕНО: normal -> dense
            fullWidth
            required
            value={password}
            onChange={e => setPassword(e.target.value)}
            InputProps={{
              endAdornment: (
                <Button
                  size="small"
                  onClick={togglePasswordVisibility}
                  sx={{ minWidth: 'auto', p: 1 }}
                  disabled={loading}
                >
                  {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                </Button>
              )
            }}
            sx={{
              mb: 3,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              }
            }}
            disabled={loading}
          />

          <Button
            type="submit"
            variant="contained"
            size="medium" // ЗМЕНШЕНО: large -> medium
            fullWidth
            startIcon={loading ? <CircularProgress size={20} color="inherit"/> : <LoginIcon />}
            sx={{
              mt: 1,
              mb: 2,
              py: 1.2, // Трохи зменшена висота кнопки
              borderRadius: 2,
              fontSize: '1rem',
              fontWeight: 600
            }}
            disabled={loading}
          >
            {loading ? t('common_loading') : t('login')}
          </Button>

          <Typography variant="body2" align="center" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
            {t('login_link_pre_text', '¿No tienes una cuenta?')}
            <Link 
              component={RouterLink} 
              to="/register"
              sx={{ 
                ml: 0.5,
                fontWeight: 600,
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              {t('register_link')}
            </Link>
          </Typography>
        </Box>

        {/* Футер */}
        <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary" align="center" display="block">
            {t('brand')} • {new Date().getFullYear()}
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
}